import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Coins } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchaseDialog({ 
  product, 
  isOpen, 
  onClose, 
  onConfirm, 
  creditBalance, 
  purchasing,
  t = (key, fallback) => fallback 
}) {
  const { user } = useAuth();
  const [priceInfo, setPriceInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && product && user) {
      setLoading(true);
      const token = localStorage.getItem('token');
      axios.get(`${API}/price-preview/${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setPriceInfo(res.data))
        .catch(() => setPriceInfo(null))
        .finally(() => setLoading(false));
    } else {
      setPriceInfo(null);
    }
  }, [isOpen, product, user]);

  if (!product) return null;

  // Calculate token-based discount using the discount percentage from API
  const discountPercent = priceInfo?.discount_percent ?? 0;
  const originalTokenPrice = product.price;
  const tokenDiscount = Math.floor(originalTokenPrice * discountPercent / 100);
  const finalTokenPrice = Math.max(1, originalTokenPrice - tokenDiscount);
  
  const hasDiscount = discountPercent > 0 && tokenDiscount > 0;
  const canAfford = creditBalance >= finalTokenPrice;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#D4AF37]" />
            {t('tokens.confirmPurchase', 'Confirm Purchase')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="p-4 bg-white/5 rounded-xl">
            <h4 className="font-medium text-lg mb-1">{product.name}</h4>
            <p className="text-gray-400 text-sm">{product.description || `${product.photos || 0} photos`}</p>
          </div>
          
          <div className="p-4 bg-[#D4AF37]/10 rounded-xl border border-[#D4AF37]/30">
            <div className="flex items-center justify-between">
              <span className="text-gray-300">{t('tokens.price', 'Price')}:</span>
              {loading ? (
                <span className="text-gray-400">Loading...</span>
              ) : hasDiscount ? (
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-[#D4AF37]" />
                    <span className="text-2xl font-bold text-[#D4AF37]">{finalTokenPrice}</span>
                    <span className="text-sm text-gray-400 line-through">{originalTokenPrice}</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Coins className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-2xl font-bold text-[#D4AF37]">{product.price}</span>
                </div>
              )}
            </div>
            
            {hasDiscount && (
              <div className="mt-2 flex items-center justify-end gap-2">
                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                  -{discountPercent}% {priceInfo?.loyalty_tier} korting
                </span>
              </div>
            )}
          </div>
          
          {hasDiscount && (
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm">
                🎉 Je bespaart <strong>{tokenDiscount}</strong> tokens met je {priceInfo?.loyalty_tier} korting!
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">{t('tokens.yourBalance', 'Your Balance')}:</span>
            <span className={`font-medium ${canAfford ? 'text-green-400' : 'text-red-400'}`}>
              {creditBalance} {t('tokens.tokenUnit', 'tokens')}
            </span>
          </div>
          
          {!canAfford && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {t('tokens.insufficientTokens', "You don't have enough tokens")}
            </div>
          )}
          
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-white/10 text-white hover:bg-white/5"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={() => onConfirm(finalTokenPrice)}
              disabled={!canAfford || purchasing}
              className="flex-1 bg-[#D4AF37] hover:bg-[#B8963A] text-black"
            >
              {purchasing ? 'Processing...' : !canAfford ? t('tokens.buyMore', 'Buy Tokens') : t('packages.buyNow', 'Buy Now')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
